import { apiRequest } from './client.js';

/**
 * Mobile Administration (M22) — which screens the mobile app shows, and how it
 * looks.
 *
 * The mobile drawer used to be built from the web application's own menu, of
 * whose 301 entries only 26 named a path the mobile build can render. Here an
 * administrator ticks, per tenant and per role, the screens that belong in the
 * app; the device picks the result up on its next sign-in, with no new build.
 */

export interface MobileScreen {
  readonly mobileScreenId: string;
  readonly screenCode: string;
  readonly route: string;
  readonly label: string;
  readonly sectionCode: string;
  readonly sortOrder: number;
  /** The tick as it stands for the scope being edited. */
  readonly isVisible: boolean;
  /** True when this tenant and role have a decision of their own. */
  readonly isExplicit: boolean;
  /** True when the value shown comes from the platform-wide default. */
  readonly inheritedFromPlatform: boolean;
}

export interface MobileScreensResponse {
  readonly tenantId: string;
  readonly roleId: string;
  readonly isPlatformScope: boolean;
  readonly items: readonly MobileScreen[];
}

/**
 * Whose settings are being edited: this tenant alone, or the default every
 * tenant inherits — including tenants onboarded after the change.
 */
export type AdminScope = 'tenant' | 'platform';

/** A tenant the platform admin can target in the "Applies to" selector. */
export interface MobileAdminTenant {
  readonly tenantId: string;
  readonly tenantCode: string;
  readonly tenantName: string | null;
  readonly isPlatform: boolean;
}

export async function listMobileAdminTenants(
  token?: string | null,
): Promise<readonly MobileAdminTenant[]> {
  const r = await apiRequest<Envelope<readonly MobileAdminTenant[]>>(
    '/v1/mobile/admin/tenants',
    { token },
  );
  return r.data ?? [];
}

/**
 * A role ticks are kept for: a product role for "All tenants" (so the tick
 * reaches that role in every tenant, including later ones), or one tenant's
 * own role for that tenant.
 */
export interface MobileAdminRole {
  readonly roleId: string;
  readonly roleCode: string;
  readonly roleName: string;
}

export async function listMobileAdminRoles(
  scope: AdminScope,
  token?: string | null,
  tenantId?: string,
): Promise<readonly MobileAdminRole[]> {
  const qs = new URLSearchParams({ scope });
  if (tenantId) qs.set('tenantId', tenantId);
  const r = await apiRequest<Envelope<readonly MobileAdminRole[]>>(
    `/v1/mobile/admin/roles?${qs.toString()}`,
    { token },
  );
  return r.data ?? [];
}

export interface MobileTheme {
  readonly tenantId: string;
  readonly appName: string;
  readonly logoUrl: string;
  readonly themeColor: string;
  readonly splashColor: string;
  readonly version: string;
}

export interface VisibilityChange {
  readonly mobileScreenId: string;
  readonly isVisible: boolean;
}

interface Envelope<T> {
  readonly success?: boolean;
  readonly data?: T;
}

export async function listMobileScreens(
  roleId: string,
  scope: AdminScope,
  token?: string | null,
  tenantId?: string,
): Promise<MobileScreensResponse> {
  const qs = new URLSearchParams({ roleId, scope });
  if (tenantId) qs.set('tenantId', tenantId);
  const r = await apiRequest<Envelope<MobileScreensResponse>>(
    `/v1/mobile/admin/screens?${qs.toString()}`,
    { token },
  );
  return (
    r.data ?? {
      tenantId: tenantId ?? '',
      roleId,
      isPlatformScope: scope === 'platform',
      items: [],
    }
  );
}

export async function saveMobileScreens(
  roleId: string,
  scope: AdminScope,
  changes: readonly VisibilityChange[],
  token?: string | null,
  tenantId?: string,
): Promise<{ updated: number }> {
  const r = await apiRequest<Envelope<{ updated: number }>>(
    '/v1/mobile/admin/screens',
    { method: 'PUT', body: { roleId, scope, tenantId, changes }, token },
  );
  return r.data ?? { updated: 0 };
}

export async function getMobileTheme(
  scope: AdminScope,
  token?: string | null,
  tenantId?: string,
): Promise<MobileTheme> {
  const qs = new URLSearchParams({ scope });
  if (tenantId) qs.set('tenantId', tenantId);
  const r = await apiRequest<Envelope<MobileTheme>>(
    `/v1/mobile/admin/theme?${qs.toString()}`,
    { token },
  );
  return (
    r.data ?? {
      tenantId: tenantId ?? '',
      appName: 'BMS',
      logoUrl: '',
      themeColor: '#0176D3',
      splashColor: '#FFFFFF',
      version: '0',
    }
  );
}

export async function saveMobileTheme(
  scope: AdminScope,
  theme: Partial<Omit<MobileTheme, 'tenantId' | 'version'>>,
  token?: string | null,
  tenantId?: string,
): Promise<MobileTheme> {
  const r = await apiRequest<Envelope<MobileTheme>>('/v1/mobile/admin/theme', {
    method: 'PUT',
    body: { ...theme, scope, tenantId },
    token,
  });
  return r.data as MobileTheme;
}

/**
 * Platform-wide field-execution policy. The geofence radius is how far (metres)
 * a painter may be from the job site and still start work; the photoMin* values
 * are the minimum execution photos per category a painter must upload before a
 * job can be completed; the completionRemark* values bound the length of the
 * painter's description of the finished work. One global rule set, so there
 * is no tenant/scope selector.
 *
 * The limits come from the parameter catalogue (t006c), the same source System
 * Config enforces, so both screens permit exactly the same values. `min`/`max`
 * are null when the catalogue states no limit.
 */
export type MobileExecutionPolicyKey =
  | 'geofenceRadiusM'
  | 'photoMinBefore'
  | 'photoMinDuring'
  | 'photoMinAfter'
  | 'photoMinLocation'
  | 'photoMinDealerCertificate'
  | 'completionRemarkMinLength'
  | 'completionRemarkMaxLength';

export interface MobileExecutionPolicyBounds {
  readonly parameterCode: string;
  readonly description: string;
  readonly default: number | null;
  readonly min: number | null;
  readonly max: number | null;
}

export type MobileExecutionPolicy = {
  readonly [K in MobileExecutionPolicyKey]: number;
} & {
  readonly bounds: {
    readonly [K in MobileExecutionPolicyKey]: MobileExecutionPolicyBounds;
  };
};

export async function getMobileExecutionPolicy(
  token?: string | null,
): Promise<MobileExecutionPolicy> {
  const r = await apiRequest<Envelope<MobileExecutionPolicy>>(
    '/v1/mobile/admin/execution-policy',
    { token },
  );
  // No stand-in values: inventing a radius here would show the administrator a
  // policy the platform is not actually running under.
  if (!r.data) {
    throw new Error(
      'The field-execution policy could not be read from the server.',
    );
  }
  return r.data;
}

export async function saveMobileExecutionPolicy(
  changes: Partial<Record<MobileExecutionPolicyKey, number>>,
  token?: string | null,
): Promise<MobileExecutionPolicy> {
  const r = await apiRequest<Envelope<MobileExecutionPolicy>>(
    '/v1/mobile/admin/execution-policy',
    { method: 'PUT', body: changes, token },
  );
  return r.data as MobileExecutionPolicy;
}

/**
 * The previous-painting distance (metres) for the "Applies to" scope: how far
 * around a new painting request's site the earlier painting — done, or still
 * in progress — is shown to the person raising it and to every approver.
 * "All tenants" sets the value every tenant without its own inherits,
 * including tenants onboarded later.
 */
export interface MobileSiteDistance {
  readonly tenantId: string;
  readonly metres: number;
  readonly bounds: MobileExecutionPolicyBounds;
}

export async function getMobileSiteDistance(
  scope: AdminScope,
  token?: string | null,
  tenantId?: string,
): Promise<MobileSiteDistance> {
  const qs = new URLSearchParams({ scope });
  if (tenantId) qs.set('tenantId', tenantId);
  const r = await apiRequest<Envelope<MobileSiteDistance>>(
    `/v1/mobile/admin/site-distance?${qs.toString()}`,
    { token },
  );
  // No stand-in value: a made-up distance would misstate what approvers see.
  if (!r.data) {
    throw new Error('The previous-painting distance could not be read from the server.');
  }
  return r.data;
}

export async function saveMobileSiteDistance(
  scope: AdminScope,
  metres: number,
  token?: string | null,
  tenantId?: string,
): Promise<MobileSiteDistance> {
  const r = await apiRequest<Envelope<MobileSiteDistance>>(
    '/v1/mobile/admin/site-distance',
    { method: 'PUT', body: { metres, scope, tenantId }, token },
  );
  return r.data as MobileSiteDistance;
}
